import React, { useState, useEffect } from 'react';
import { apiService, Country } from '../services/api';

// Valid ISO 4217 currency codes
const VALID_CURRENCY_CODES = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL',
  'BSD', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLP', 'CNY',
  'COP', 'CRC', 'CUC', 'CUP', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD',
  'EGP', 'ERN', 'ETB', 'EUR', 'FJD', 'FKP', 'GBP', 'GEL', 'GGP', 'GHS',
  'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
  'IDR', 'ILS', 'IMP', 'INR', 'IQD', 'IRR', 'ISK', 'JEP', 'JMD', 'JOD',
  'JPY', 'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
  'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD', 'MAD', 'MDL', 'MGA', 'MKD',
  'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
  'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'OMR', 'PAB', 'PEN', 'PGK',
  'PHP', 'PKR', 'PLN', 'PYG', 'QAR', 'RON', 'RSD', 'RUB', 'RWF', 'SAR',
  'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLL', 'SOS', 'SPL', 'SRD',
  'STN', 'SYP', 'SZL', 'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD',
  'TVD', 'TWD', 'TZS', 'UAH', 'UGX', 'USD', 'UYU', 'UZS', 'VEF', 'VND',
  'VUV', 'WST', 'XAF', 'XCD', 'XDR', 'XOF', 'XPF', 'YER', 'ZAR', 'ZMW', 'ZWL'
];

const Countries: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState<{ code: Country[]; name: Country[] }>({ code: [], name: [] });

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    currency: '',
  });

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllCountriesForManagement();
      // Ensure data is an array
      if (Array.isArray(data)) {
        setCountries(data);
        setError(null);
      } else {
        console.error('Expected array but got:', data);
        setCountries([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      setError('Failed to load countries');
      console.error('Error loading countries:', err);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation for creating new country
    if (!editingCountry) {
      if (!formData.code || !formData.name || !formData.currency) {
        setError('All fields are required');
        return;
      }

      if (formData.code.length !== 2) {
        setError('Country code must be exactly 2 characters');
        return;
      }

      if (formData.currency.length !== 3) {
        setError('Currency code must be exactly 3 characters');
        return;
      }

      // Validate currency code format (ISO 4217)
      const validCurrencyRegex = /^[A-Z]{3}$/;
      if (!validCurrencyRegex.test(formData.currency)) {
        setError('Currency code must be 3 uppercase letters (e.g., EUR, USD, GBP)');
        return;
      }

      // Validate currency code exists in ISO 4217 standard
      if (!VALID_CURRENCY_CODES.includes(formData.currency)) {
        setError(`Invalid currency code '${formData.currency}'. Please use a valid ISO 4217 currency code (e.g., EUR, USD, GBP)`);
        return;
      }
    } else {
      // Validation for editing existing country
      if (!formData.name) {
        setError('Country name is required');
        return;
      }

      if (formData.code && formData.code.trim() !== '' && formData.code !== editingCountry.code && formData.code.length !== 2) {
        setError('Country code must be exactly 2 characters');
        return;
      }

      if (formData.currency && formData.currency.trim() !== '' && formData.currency !== editingCountry.currency && formData.currency.length !== 3) {
        setError('Currency code must be exactly 3 characters');
        return;
      }

      if (formData.currency && formData.currency.trim() !== '' && formData.currency !== editingCountry.currency) {
        // Validate currency code format (ISO 4217)
        const validCurrencyRegex = /^[A-Z]{3}$/;
        if (!validCurrencyRegex.test(formData.currency)) {
          setError('Currency code must be 3 uppercase letters (e.g., EUR, USD, GBP)');
          return;
        }

        // Validate currency code exists in ISO 4217 standard
        if (!VALID_CURRENCY_CODES.includes(formData.currency)) {
          setError(`Invalid currency code '${formData.currency}'. Please use a valid ISO 4217 currency code (e.g., EUR, USD, GBP)`);
          return;
        }
      }
    }

    try {
      if (editingCountry) {
        // Only send fields that have been changed and are valid
        const updateData: any = {};
        if (formData.name !== editingCountry.name) updateData.name = formData.name;
        if (formData.code !== editingCountry.code && formData.code && formData.code.length === 2) updateData.code = formData.code;
        if (formData.currency !== editingCountry.currency && formData.currency && formData.currency.length === 3) updateData.currency = formData.currency;
        await apiService.updateCountryForManagement(editingCountry.id, updateData);
      } else {
        await apiService.createCountryForManagement(formData);
      }
      
      setShowForm(false);
      setEditingCountry(null);
      setFormData({ code: '', name: '', currency: '' });
      loadCountries();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save country');
    }
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({
      code: country.code,
      name: country.name,
      currency: country.currency,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this country?')) {
      return;
    }

    try {
      await apiService.deleteCountryForManagement(id);
      loadCountries();
      setError(null);
    } catch (err) {
      setError('Failed to delete country');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await apiService.deactivateCountryForManagement(id);
      loadCountries();
      setError(null);
    } catch (err) {
      setError('Failed to deactivate country');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await apiService.activateCountryForManagement(id);
      loadCountries();
      setError(null);
    } catch (err) {
      setError('Failed to activate country');
    }
  };

  const handleFindDuplicates = async () => {
    try {
      const data = await apiService.findCountryDuplicatesForManagement();
      setDuplicates(data);
      setShowDuplicates(true);
      setError(null);
    } catch (err) {
      setError('Failed to find duplicates');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCountry(null);
    setFormData({ code: '', name: '', currency: '' });
    setError(null);
  };

  if (loading) {
    return <div className="p-6">Loading countries...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Countries Management</h1>
        <div className="flex gap-4">
          <button
            onClick={handleFindDuplicates}
            style={{ backgroundColor: '#3b82f6', border: 'none' }}
            className="px-4 py-2 text-white rounded hover:bg-blue-600"
          >
            Find Duplicates
          </button>
          <button
            onClick={() => setShowForm(true)}
            style={{ backgroundColor: '#10b981', border: 'none' }}
            className="px-4 py-2 text-white rounded hover:bg-green-600"
          >
            Add New
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-6 bg-white border rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">
            {editingCountry ? 'Edit Country' : 'Add New Country'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country Code (3 chars)
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  className="border border-gray-300 rounded px-3 py-2"
                  maxLength={3}
                  placeholder="e.g., USA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  className="border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., United States"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency Code (3 chars)
                </label>
                <input
                  type="text"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                  style={{ width: '100%', minWidth: '100%', maxWidth: '100%' }}
                  className="border border-gray-300 rounded px-3 py-2"
                  maxLength={3}
                  placeholder="e.g., USD"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <button
                type="submit"
                style={{ backgroundColor: '#10b981', border: 'none' }}
                className="px-4 py-2 text-white rounded hover:bg-green-600"
              >
                {editingCountry ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Currency
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(countries) && countries.map((country) => (
              <tr key={country.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {country.code}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {country.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {country.currency}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    country.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {country.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(country.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleEdit(country)}
                      style={{ border: 'none' }}
                      className="text-indigo-600 hover:text-indigo-900 p-2 rounded hover:bg-indigo-50 transition-colors"
                      title="Edit country"
                    >
                      <span className="text-xl">✏️</span>
                    </button>
                    {country.isActive ? (
                      <button
                        onClick={() => handleDeactivate(country.id)}
                        style={{ border: 'none' }}
                        className="text-yellow-600 hover:text-yellow-900 p-2 rounded hover:bg-yellow-50 transition-colors"
                        title="Deactivate country"
                      >
                        <span className="text-xl">⏸️</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivate(country.id)}
                        style={{ border: 'none' }}
                        className="text-green-600 hover:text-green-900 p-2 rounded hover:bg-green-50 transition-colors"
                        title="Activate country"
                      >
                        <span className="text-xl">▶️</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(country.id)}
                      style={{ border: 'none' }}
                      className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                      title="Delete country"
                    >
                      <span className="text-xl">🗑️</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Duplicates Modal */}
      {showDuplicates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Duplicate Countries Found</h2>
              <button
                onClick={() => setShowDuplicates(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {duplicates.code.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Duplicate Country Codes:</h3>
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  {duplicates.code.map((country, index) => (
                    <div key={index} className="mb-2">
                      <strong>{country.code}</strong> - {country.name} (ID: {country.id})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {duplicates.name.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Duplicate Country Names:</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  {duplicates.name.map((country, index) => (
                    <div key={index} className="mb-2">
                      <strong>{country.name}</strong> - {country.code} (ID: {country.id})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {duplicates.code.length === 0 && duplicates.name.length === 0 && (
              <div className="text-center text-gray-500">
                No duplicates found!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Countries;
